import { Button } from "@/components/ui/button"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { IconLoader2 } from "@tabler/icons-react"
import { CheckCircle, X } from "lucide-react"
import { useEffect } from "react"
import { ImportRowList } from "./ImportRowList"
import type { ImportRow } from "./useDeclarationImport"

/** How long a fully clean run stays visible before the card leaves on its own. */
const CLEAN_RUN_LINGER_MS = 2_500

/**
 * The overview's inline import: a card above the list that appears the moment
 * files land on the page, counts the uploads up, and either leaves on its own
 * (every file made it; the toast and the new "processing" rows say the rest)
 * or stays with the refusals until dismissed, so no failure is ever read off
 * a toast that is already gone.
 */
export function DeclarationImportCard({
  rows,
  isImporting,
  onClose,
}: {
  rows: ImportRow[]
  isImporting: boolean
  onClose: () => void
}) {
  const { translate } = useTranslation()
  const doneCount = rows.filter((r) => r.state === "done").length
  const failedCount = rows.filter((r) => r.state === "failed").length
  const finished = rows.length > 0 && !isImporting
  const clean = finished && failedCount === 0

  useEffect(() => {
    if (!clean) return undefined
    const id = setTimeout(onClose, CLEAN_RUN_LINGER_MS)
    return () => clearTimeout(id)
  }, [clean, onClose])

  return (
    <section
      data-testid="declaration-import-card"
      aria-live="polite"
      className="animate-in fade-in-0 slide-in-from-top-2 flex flex-col gap-3 rounded-xl border border-grey4 bg-grey2 p-4 duration-300"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 text-[14px] text-white">
          {isImporting ? (
            <IconLoader2 size={16} className="animate-spin text-yellow1" />
          ) : (
            <CheckCircle
              size={16}
              className={failedCount ? "text-warning" : "text-success"}
            />
          )}
          <span>
            {isImporting
              ? translate("MC_DECL_IMPORT_RUNNING", {
                  done: doneCount + failedCount,
                  total: rows.length,
                })
              : translate("MC_DECL_IMPORT_SUMMARY", {
                  uploaded: doneCount,
                  failed: failedCount,
                })}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          aria-label={translate("CLOSE")}
          className="h-7 w-7 shrink-0 p-0 text-grey5 hover:text-white"
          disabled={isImporting}
          onClick={onClose}
        >
          <X size={14} />
        </Button>
      </div>
      {finished && doneCount > 0 && (
        <p className="text-[12.5px] text-grey5">
          {translate("MC_DECL_IMPORT_EXTRACTION_RUNNING")}
        </p>
      )}
      <ImportRowList rows={rows} className="max-h-[40vh]" />
    </section>
  )
}
